import { Box, Collapse, Stack, Typography } from '@mui/material';
import { Paper } from '@shared/components';
import { sideFiltersWidth } from '@shared/styles';
import {
  Children,
  forwardRef,
  Fragment,
  isValidElement,
  PropsWithChildren,
  ReactNode,
} from 'react';
import { SystemStyleObject } from '@mui/system';

interface SideFilterProps extends PropsWithChildren {
  heading?: string;
  showFilters: boolean;
  footer?: ReactNode;
  sx?: SystemStyleObject;
}

/** Panels with at most this many filters get the roomier spacing. */
const spaciousFilterCount = 6;
const filterSectionClass = 'SideFilters-section';

function flattenFilters(children: ReactNode, prefix = ''): { key: string; filter: ReactNode }[] {
  return Children.toArray(children).flatMap((child, index) => {
    const key = `${prefix}${isValidElement(child) ? child.key ?? index : index}`;
    return isValidElement(child) && child.type === Fragment
      ? flattenFilters((child.props as PropsWithChildren).children, key)
      : [{ key, filter: child }];
  });
}

export const SideFilters = forwardRef<HTMLDivElement, SideFilterProps>(
  ({ heading, showFilters, footer, children, sx }, ref) => {
    const filters = flattenFilters(children);

    const spacing = filters.length <= spaciousFilterCount ? 2 : 1;

    return (
      <Collapse orientation="horizontal" in={showFilters} mountOnEnter unmountOnExit>
        <Paper
          ref={ref}
          paddingY="none"
          sx={{
            display: 'flex',
            height: '100%',
            flexDirection: 'column',
            overflow: 'auto',
            borderRadius: 0,
            borderLeft: '1px solid',
            borderColor: 'divider',
            width: sideFiltersWidth,
            minWidth: sideFiltersWidth,
            px: 2,
            py: 1.5,
            ...sx,
          }}
        >
          <Stack gap={1} flexGrow={1} minHeight={0}>
            {heading && <Typography variant="subtitle1">{heading}</Typography>}

            <Stack
              sx={{
                [`& > .${filterSectionClass}:empty`]: { display: 'none' },
                [`& > .${filterSectionClass}:not(:empty) ~ .${filterSectionClass}:not(:empty)`]: {
                  mt: spacing,
                  pt: spacing,
                },
              }}
            >
              {filters.map(({ key, filter }) => (
                <Box
                  key={key}
                  className={filterSectionClass}
                  sx={{ display: 'flex', flexDirection: 'column' }}
                >
                  {filter}
                </Box>
              ))}
            </Stack>

            {footer}
          </Stack>
        </Paper>
      </Collapse>
    );
  }
);
